// The Union API contract. Routes and schemas defined here are the single source
// for the OpenAPI document (openapi.json), which generates the TypeScript types
// for the web apps and the Swift client for the Apple apps.
//
// Change a route here, run `pnpm contracts`, and commit the regenerated files.

import { createRoute, z } from "@hono/zod-openapi";
import { isValidClubSlug } from "./tenancy.ts";

export * from "./tenancy.ts";

// ---------------------------------------------------------------------------
// Shared schemas
// ---------------------------------------------------------------------------

export const ErrorSchema = z
  .object({
    error: z.object({
      code: z.string().openapi({ example: "not_found" }),
      message: z.string(),
      requestId: z.string(),
    }),
  })
  .openapi("Error");

const ClubSlug = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/)
  .openapi({ example: "cedar-ridge" });

const LocalDate = z.iso
  .date()
  .openapi({ description: "Calendar date in the club's timezone", example: "2026-10-24" });

export const ClubSummarySchema = z
  .object({
    id: z.uuid(),
    slug: ClubSlug,
    name: z.string(),
    timezone: z.string().openapi({ example: "America/New_York" }),
  })
  .openapi("ClubSummary");

export const StaffRoleSchema = z
  .enum(["owner", "admin", "head_pro", "starter", "pro_shop"])
  .openapi("StaffRole");

export const MyMembershipSchema = z
  .object({
    id: z.uuid(),
    memberNumber: z.string(),
    status: z.enum(["active", "suspended", "ended"]),
    club: ClubSummarySchema,
  })
  .openapi("MyMembership");

export const MeSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    memberships: z.array(MyMembershipSchema),
    staffClubs: z.array(
      z.object({
        club: ClubSummarySchema,
        roles: z.array(StaffRoleSchema),
      }),
    ),
  })
  .openapi("Me");

export const CourseKindSchema = z.enum(["course", "simulator"]).openapi("CourseKind", {
  description:
    "What a club books by time slot: a golf course, or a simulator (no holes or scoring; slots up to 2 hours)",
});

const TeeTimeBase = {
  id: z.uuid(),
  startsAt: z.iso.datetime({ offset: true }),
  localTime: z.string().openapi({ description: "HH:MM in the club's timezone", example: "07:09" }),
  courseId: z.uuid(),
  courseName: z.string(),
  maxPlayers: z.int(),
  blocked: z.boolean().openapi({ description: "Closed to member booking (maintenance, an event…)" }),
};

export const PlayOptionSchema = z
  .object({
    id: z.uuid(),
    name: z.string().openapi({ example: "Riding cart" }),
    isDefault: z.boolean().openapi({ description: "New bookings get this unless the member chose before" }),
  })
  .openapi("PlayOption", {
    description: "How a player is playing: riding cart, push cart, caddie, walking…",
  });

export const MemberTeeTimeSchema = z
  .object({
    ...TeeTimeBase,
    // Members see who is playing, but only a display name: no member numbers
    // or contact details.
    players: z.array(z.object({ position: z.int(), displayName: z.string(), isMe: z.boolean() })),
    myPlayOptionId: z
      .uuid()
      .nullable()
      .openapi({ description: "How the member is playing, if they're on it" }),
    myNote: z.string().nullable().openapi({ description: "The member's note on their own booking (G3)" }),
    myGuests: z
      .array(z.object({ bookingId: z.uuid(), name: z.string(), playOptionId: z.uuid().nullable() }))
      .openapi({
        description:
          "Guests the member brought on this time (E7): to set how each is playing (setMemberPlayOption) or remove",
      }),
    eventName: z.string().nullable().openapi({
      description: "The event on this time, when the club shows its name to members (D5)",
      example: "Ladies' league",
    }),
    myBookingId: z.uuid().nullable().openapi({
      description: "The member's own booking on this time, if they're on it (to switch or remove)",
    }),
    restriction: z
      .object({
        code: z.enum(["not_for_group", "window_not_open", "too_close", "already_started", "lottery"]),
        message: z.string().openapi({
          description: "Why, in words for the member",
          example: "Social members can book from 12:00 on Mondays",
        }),
        opensAt: z.iso
          .datetime({ offset: true })
          .nullable()
          .openapi({ description: "When it opens, if it will" }),
        switchFrom: z
          .object({ bookingId: z.uuid(), localTime: z.string().openapi({ example: "07:27" }) })
          .nullable()
          .openapi({
            description:
              "For `too_close`: the member's own booking in the way. They can move it here instead (moveMemberBooking).",
          }),
      })
      .nullable()
      .openapi({
        description:
          "Why the member's group rules don't let them book this time; null when they do (it may still be full or blocked)",
      }),
  })
  .openapi("MemberTeeTime");

export const EventColourSchema = z
  .enum(["green", "heather", "teal", "sand", "red", "grey"])
  .openapi("EventColour", {
    description:
      "From the design system's tested pairs: primary, secondary, success, warning, destructive, muted",
  });

export const EventTypeSchema = z
  .object({ id: z.uuid(), name: z.string(), colour: EventColourSchema })
  .openapi("EventType", { description: "One of the club's own event types (D4)" });

export const ArrivalSchema = z.enum(["arrived", "no_show"]).openapi("Arrival");

export const StaffTeeTimeSchema = z
  .object({
    ...TeeTimeBase,
    blockedReason: z.string().nullable(),
    closureId: z.uuid().nullable().openapi({ description: "The closure that blocked it (H5), if any" }),
    event: z
      .object({ id: z.uuid(), name: z.string(), type: EventTypeSchema })
      .nullable()
      .openapi({ description: "The event that has reserved this time (D1), if any" }),
    players: z.array(
      z
        .object({
          bookingId: z.uuid(),
          position: z.int(),
          name: z.string().openapi({ description: "Member's name as the club has it, or the guest's" }),
          membershipId: z.uuid().nullable().openapi({ description: "Null for guests" }),
          memberNumber: z.string().nullable(),
          guestOf: z.string().nullable().openapi({ description: "For guests: the host member's name" }),
          playOption: z
            .object({ id: z.uuid(), name: z.string() })
            .nullable()
            .openapi({ description: "How they're playing (G2)" }),
          note: z
            .string()
            .nullable()
            .openapi({ description: "The booking's note for the starter (G3)", example: "Playing 9 holes" }),
          arrival: ArrivalSchema.nullable().openapi({
            description: "Checked in, or a no-show (H1); null: not yet",
          }),
        })
        .openapi("StaffPlayer"),
    ),
  })
  .openapi("StaffTeeTime");

const teeSheetFields = {
  club: ClubSummarySchema,
  date: LocalDate,
  courses: z.array(z.object({ id: z.uuid(), name: z.string(), kind: CourseKindSchema })).openapi({
    description: "The courses and simulators to show that day, golf courses first; each has its own sheet",
  }),
  playOptions: z.array(PlayOptionSchema).openapi({ description: "The club's list, in order" }),
};

const Instant = z.iso.datetime({ offset: true });
export const LotteryRequestSchema = z
  .object({
    id: z.uuid(),
    courseId: z.uuid(),
    date: LocalDate,
    earliest: z.string().openapi({ description: "Club-local HH:MM", example: "07:30" }),
    latest: z.string().openapi({ description: "Club-local HH:MM", example: "09:00" }),
    laterOk: z.boolean().openapi({ description: "A later time is fine if the range is full" }),
    players: z
      .array(
        z.object({
          membershipId: z.uuid(),
          name: z.string(),
          memberNumber: z.string().nullable(),
          isMe: z.boolean(),
          isOrganizer: z.boolean(),
        }),
      )
      .openapi({ description: "The group, the organizer first" }),
    createdAt: Instant,
    draw: z
      .object({
        position: z.int().openapi({ description: "Its place in the draw order, from 1" }),
        teeTimeId: z.uuid().nullable().openapi({ description: "Null: not placed" }),
        localTime: z.string().nullable().openapi({ description: "Club-local HH:MM of that tee time" }),
        reason: z.string().openapi({ example: "Placed in its range" }),
      })
      .nullable()
      .openapi({ description: "F3. Staff see it once drawn; members once the sheet is revealed." }),
  })
  .openapi("LotteryRequest", { description: "A group's request for a lottery date (F2)" });

export const LotteryStatusSchema = z
  .object({
    courseId: z.uuid(),
    requestsOpenAt: Instant,
    requestsCloseAt: Instant,
    revealsAt: Instant,
    drawnAt: Instant.nullable().openapi({ description: "When the draw last ran (F3); null: not yet" }),
    revealedAt: Instant.nullable().openapi({
      description: "When the sheet was revealed and placements booked (F4); until then members can't book",
    }),
    requests: z.array(LotteryRequestSchema).openapi({
      description: "Staff: every request for this course and date, oldest first. Members: their own, if any.",
    }),
  })
  .openapi("LotteryStatus", { description: "This date is a lottery date on this course (F1)" });

export const MemberTeeSheetSchema = z
  .object({
    ...teeSheetFields,
    teeTimes: z.array(MemberTeeTimeSchema),
    lotteries: z
      .array(LotteryStatusSchema)
      .openapi({ description: "Courses where this date is a lottery date, with the member's request" }),
  })
  .openapi("MemberTeeSheet");
export const ClosureSchema = z
  .object({
    id: z.uuid(),
    courseId: z.uuid(),
    from: z.string().openapi({ description: "HH:MM, club time", example: "13:00" }),
    reason: z.string().openapi({ example: "Weather" }),
    closedAt: z.iso.datetime({ offset: true }),
    closedBy: z.string().nullable(),
    reopenedAt: z.iso.datetime({ offset: true }).nullable(),
    players: z
      .array(
        z.object({
          localTime: z.string(),
          name: z.string(),
          memberNumber: z.string().nullable().openapi({ description: "Null for guests" }),
          guestOf: z.string().nullable(),
        }),
      )
      .openapi({ description: "Whose bookings it cancelled, to tell them (notifications come later)" }),
  })
  .openapi("Closure", { description: "The rest of a day closed on a course (H5)" });

export const StaffTeeSheetSchema = z
  .object({
    ...teeSheetFields,
    teeTimes: z.array(StaffTeeTimeSchema),
    closures: z.array(ClosureSchema).openapi({ description: "Closures that day, oldest first" }),
    lotteries: z
      .array(LotteryStatusSchema)
      .openapi({ description: "Courses where this date is a lottery date" }),
  })
  .openapi("StaffTeeSheet");

const errorResponses = {
  400: { description: "Invalid request", content: { "application/json": { schema: ErrorSchema } } },
  401: { description: "Not signed in", content: { "application/json": { schema: ErrorSchema } } },
  404: {
    description: "Not found, or not visible to you",
    content: { "application/json": { schema: ErrorSchema } },
  },
};

const bearer = [{ bearerAuth: [] }];

const clubSlugParams = z.object({ clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }) });

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

export const healthRoute = createRoute({
  method: "get",
  path: "/health",
  operationId: "getHealth",
  tags: ["system"],
  responses: {
    200: {
      description: "Service is up",
      content: {
        "application/json": { schema: z.object({ status: z.literal("ok") }).openapi("Health") },
      },
    },
  },
});

export const getMeRoute = createRoute({
  method: "get",
  path: "/me",
  operationId: "getMe",
  tags: ["identity"],
  security: bearer,
  responses: {
    200: {
      description: "The signed-in person, every club membership and staff role",
      content: { "application/json": { schema: MeSchema } },
    },
    ...errorResponses,
  },
});

const teeSheetParams = {
  params: z.object({ clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }) }),
  query: z.object({ date: LocalDate.openapi({ param: { name: "date", in: "query" } }) }),
};

export const getMemberTeeSheetRoute = createRoute({
  method: "get",
  path: "/member/clubs/{clubSlug}/tee-sheet",
  operationId: "getMemberTeeSheet",
  tags: ["member"],
  security: bearer,
  request: teeSheetParams,
  responses: {
    200: {
      description: "The club's tee sheet for one day, member view",
      content: { "application/json": { schema: MemberTeeSheetSchema } },
    },
    ...errorResponses,
  },
});

export const getStaffTeeSheetRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/tee-sheet",
  operationId: "getStaffTeeSheet",
  tags: ["staff"],
  security: bearer,
  request: teeSheetParams,
  responses: {
    200: {
      description: "The club's tee sheet for one day, staff view",
      content: { "application/json": { schema: StaffTeeSheetSchema } },
    },
    ...errorResponses,
  },
});

export const BookingSchema = z
  .object({
    id: z.uuid(),
    teeTimeId: z.uuid(),
    position: z.int(),
    membershipId: z.uuid().nullable().openapi({ description: "Null when the booking is for a guest" }),
    createdAt: z.iso.datetime({ offset: true }),
  })
  .openapi("Booking");

export const createMemberBookingRoute = createRoute({
  method: "post",
  path: "/member/clubs/{clubSlug}/tee-times/{teeTimeId}/bookings",
  operationId: "createMemberBooking",
  tags: ["member"],
  security: bearer,
  description:
    "Books the signed-in member into the lowest open position. Send a fresh UUID in Idempotency-Key " +
    "for each booking attempt and reuse it on retries: a retry returns the original booking (200) " +
    "instead of booking twice.",
  request: {
    params: z.object({
      clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }),
      teeTimeId: z.uuid().openapi({ param: { name: "teeTimeId", in: "path" } }),
    }),
    headers: z.object({
      "idempotency-key": z.uuid().openapi({ param: { name: "idempotency-key", in: "header" } }),
    }),
  },
  responses: {
    201: { description: "Booked", content: { "application/json": { schema: BookingSchema } } },
    200: {
      description: "Already booked by this same request (a retry); the original booking",
      content: { "application/json": { schema: BookingSchema } },
    },
    409: {
      description:
        "Not booked: `tee_time_full`, `already_booked`, `window_not_open`, `not_for_group` (the member's " +
        "group can't book this time), `too_close` (another of their bookings that day is too near), `already_started`, `tee_time_blocked`, or `idempotency_key_reused`",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const moveMemberBookingRoute = createRoute({
  method: "put",
  path: "/member/clubs/{clubSlug}/bookings/{bookingId}/tee-time",
  operationId: "moveMemberBooking",
  tags: ["member"],
  security: bearer,
  description:
    "Moves the signed-in member's own booking to another tee time (e.g. 07:27 to 08:12). Checked like a new " +
    "booking, with the old time treated as free. Safe to retry: moving to where it already is returns it.",
  request: {
    params: z.object({
      clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }),
      bookingId: z.uuid().openapi({ param: { name: "bookingId", in: "path" } }),
    }),
    body: {
      required: true,
      content: { "application/json": { schema: z.object({ teeTimeId: z.uuid() }).openapi("MemberMove") } },
    },
  },
  responses: {
    200: { description: "Moved", content: { "application/json": { schema: BookingSchema } } },
    409: {
      description:
        "Not moved: `tee_time_full`, `already_booked`, `tee_time_blocked`, `not_for_group`, `window_not_open` " +
        "or `too_close`",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const cancelMemberBookingRoute = createRoute({
  method: "delete",
  path: "/member/clubs/{clubSlug}/bookings/{bookingId}",
  operationId: "cancelMemberBooking",
  tags: ["member"],
  security: bearer,
  description:
    "Removes the signed-in member from a tee time they're on, with the guests they brought, until it " +
    "starts. On a guest's booking, removes just that guest (E7).",
  request: {
    params: z.object({
      clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }),
      bookingId: z.uuid().openapi({ param: { name: "bookingId", in: "path" } }),
    }),
  },
  responses: {
    200: {
      description: "Removed (the audit log keeps what it was)",
      content: {
        "application/json": { schema: z.object({ id: z.uuid() }).openapi("MemberBookingCancelled") },
      },
    },
    409: {
      description: "`already_started`: the tee time has started",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const MemberGuestSchema = z
  .object({
    name: z.string().trim().min(1).max(80).openapi({ example: "Sam Rivera" }),
    email: z
      .email()
      .optional()
      .openapi({ description: "So the club knows it's the same guest next time (guest limits per year)" }),
    playOptionId: z
      .uuid()
      .optional()
      .openapi({ description: "How the guest is playing (G2); default: the club's default option" }),
  })
  .openapi("MemberGuest");

export const addMemberGuestRoute = createRoute({
  method: "post",
  path: "/member/clubs/{clubSlug}/tee-times/{teeTimeId}/guests",
  operationId: "addMemberGuest",
  tags: ["member"],
  security: bearer,
  description:
    "Adds a guest to a tee time the signed-in member is on, within the club's guest rules (E7). Safe to " +
    "retry: the same guest on the same time returns that booking. The member removes a guest with " +
    "cancelMemberBooking.",
  request: {
    params: z.object({
      clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }),
      teeTimeId: z.uuid().openapi({ param: { name: "teeTimeId", in: "path" } }),
    }),
    body: { required: true, content: { "application/json": { schema: MemberGuestSchema } } },
  },
  responses: {
    201: { description: "Added", content: { "application/json": { schema: BookingSchema } } },
    409: {
      description:
        "Not added (the message says why, for the member): `not_on_time`, `guest_limit`, `guest_hours`, " +
        "`guest_rounds`, `tee_time_full`, `tee_time_blocked`, `lottery` or `already_started`",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

// ---------------------------------------------------------------------------
// Scoring. Designed for offline use: the device creates the round's ID, keeps
// every score locally, and syncs whenever it has signal. Sync is idempotent:
// resending changes nothing, and per hole the most recently entered score wins.
// ---------------------------------------------------------------------------

const CourseHoleSchema = z.object({ number: z.int(), par: z.int() }).openapi("CourseHole");

export const CourseSchema = z
  .object({ id: z.uuid(), name: z.string(), holes: z.array(CourseHoleSchema) })
  .openapi("Course");

const HoleScoreSchema = z
  .object({
    hole: z.int().min(1).max(36),
    strokes: z.int().min(1).max(20),
    recordedAt: z.iso
      .datetime({ offset: true })
      .openapi({ description: "When the score was entered on the device. The latest entry per hole wins." }),
  })
  .openapi("HoleScore");

export const ScorecardSchema = z
  .object({
    roundId: z.uuid(),
    clubSlug: ClubSlug,
    startedAt: z.iso.datetime({ offset: true }),
    course: CourseSchema,
    scores: z.array(HoleScoreSchema),
    totalStrokes: z.int(),
    toPar: z.int().openapi({ description: "Strokes relative to par over the holes played so far" }),
  })
  .openapi("Scorecard");

export const RoundSyncSchema = z
  .object({
    clubSlug: ClubSlug,
    courseId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    scores: z.array(HoleScoreSchema).max(72),
  })
  .openapi("RoundSync");

export const getMemberCoursesRoute = createRoute({
  method: "get",
  path: "/member/clubs/{clubSlug}/courses",
  operationId: "getMemberCourses",
  tags: ["member"],
  security: bearer,
  request: { params: z.object({ clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }) }) },
  responses: {
    200: {
      description: "The club's courses with hole-by-hole pars",
      content: { "application/json": { schema: z.array(CourseSchema) } },
    },
    ...errorResponses,
  },
});

const roundIdParam = z.object({
  roundId: z.uuid().openapi({ param: { name: "roundId", in: "path" }, description: "Created by the device" }),
});

export const syncMemberRoundRoute = createRoute({
  method: "put",
  path: "/member/rounds/{roundId}",
  operationId: "syncMemberRound",
  tags: ["member"],
  security: bearer,
  description:
    "Creates the round if it's new and merges the scores. Safe to resend: per hole, the score with " +
    "the latest recordedAt wins, so a stale retry never overwrites a newer entry.",
  request: {
    params: roundIdParam,
    body: { content: { "application/json": { schema: RoundSyncSchema } }, required: true },
  },
  responses: {
    200: {
      description: "The scorecard after merging",
      content: { "application/json": { schema: ScorecardSchema } },
    },
    409: {
      description: "`round_mismatch`: this round ID already belongs to a different club or course",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const getMemberRoundRoute = createRoute({
  method: "get",
  path: "/member/rounds/{roundId}",
  operationId: "getMemberRound",
  tags: ["member"],
  security: bearer,
  request: { params: roundIdParam },
  responses: {
    200: {
      description: "The round's scorecard",
      content: { "application/json": { schema: ScorecardSchema } },
    },
    ...errorResponses,
  },
});

// ---------------------------------------------------------------------------
// Public (no sign-in): what a club site needs before anyone signs in.
// ---------------------------------------------------------------------------

export const PublicClubSchema = z
  .object({
    slug: ClubSlug,
    name: z.string(),
    timezone: z.string(),
    signInClientId: z
      .uuid()
      .nullable()
      .openapi({ description: "OAuth client ID for this club's member site; null until the club is set up" }),
  })
  .openapi("PublicClub");

export const getPublicClubRoute = createRoute({
  method: "get",
  path: "/public/clubs/{clubSlug}",
  operationId: "getPublicClub",
  tags: ["public"],
  request: { params: z.object({ clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }) }) },
  responses: {
    200: {
      description: "The club's public details",
      content: { "application/json": { schema: PublicClubSchema } },
    },
    404: { description: "No such club", content: { "application/json": { schema: ErrorSchema } } },
  },
});

// ---------------------------------------------------------------------------
// Staff booking: any staff role adds members or guests, moves and cancels.
// Staff aren't bound by booking windows or blocks; capacity still holds.
// ---------------------------------------------------------------------------

export const RosterMemberSchema = z
  .object({ membershipId: z.uuid(), name: z.string(), memberNumber: z.string() })
  .openapi("RosterMember");

export const searchRosterRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/members",
  operationId: "searchRoster",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams,
    query: z.object({
      q: z.string().trim().min(1).max(60).openapi({ description: "Part of a name or member number" }),
    }),
  },
  responses: {
    200: {
      description: "Up to 20 active members",
      content: {
        "application/json": {
          schema: z.object({ members: z.array(RosterMemberSchema) }).openapi("RosterSearch"),
        },
      },
    },
    ...errorResponses,
  },
});

// --- Find a member fast (H2): their upcoming times and recent history, for the phone.

const MemberTeeTimeSummarySchema = z
  .object({
    bookingId: z.uuid(),
    teeTimeId: z.uuid(),
    date: LocalDate,
    localTime: z.string().openapi({ example: "07:36" }),
    courseId: z.uuid(),
    courseName: z.string(),
    with: z.array(z.string()).openapi({ description: "The other players' names (guests too)" }),
    playOption: z.string().nullable(),
    arrival: ArrivalSchema.nullable(),
  })
  .openapi("MemberTeeTimeSummary");

export const MemberDetailSchema = z
  .object({
    membershipId: z.uuid(),
    name: z.string(),
    memberNumber: z.string(),
    email: z.string().nullable().openapi({ description: "The club's record; staff only" }),
    group: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    status: z.string().openapi({ example: "active" }),
    signsIn: z.boolean().openapi({ description: "Has claimed their membership (can book online)" }),
    noShows: z.int().openapi({ description: "In the last 12 months" }),
    upcoming: z
      .array(MemberTeeTimeSummarySchema)
      .openapi({ description: "From now on, soonest first (up to 20)" }),
    recent: z
      .array(MemberTeeTimeSummarySchema)
      .openapi({ description: "The last 90 days, most recent first (up to 20)" }),
  })
  .openapi("MemberDetail");

export const getMemberDetailRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/members/{membershipId}",
  operationId: "getMemberDetail",
  tags: ["staff"],
  security: bearer,
  description: "Any staff role. Another club's member: 404.",
  request: {
    params: clubSlugParams.extend({
      membershipId: z.uuid().openapi({ param: { name: "membershipId", in: "path" } }),
    }),
  },
  responses: {
    200: { description: "The member", content: { "application/json": { schema: MemberDetailSchema } } },
    ...errorResponses,
  },
});

export const TeeTimeHistorySchema = z
  .object({
    teeTime: z.object({ id: z.uuid(), date: LocalDate, localTime: z.string(), courseName: z.string() }),
    entries: z
      .array(
        z.object({
          at: z.iso.datetime({ offset: true }),
          action: z.string().openapi({ example: "booking.move" }),
          player: z.string().nullable().openapi({
            description: "Who it was about; null for the tee time itself",
            example: "Ava Hughes",
          }),
          summary: z.string().openapi({ description: "What happened, in words", example: "Moved to 08:12" }),
          by: z.string().openapi({ description: "Who did it", example: "Riley Chen" }),
          byStaff: z.boolean().openapi({ description: "Done by staff (otherwise the member)" }),
        }),
      )
      .openapi({ description: "Oldest first" }),
  })
  .openapi("TeeTimeHistory", { description: "Who booked, moved or cancelled whom, and when (G7)" });

export const CloseRestOfDaySchema = z
  .object({
    courseId: z.uuid().optional().openapi({ description: "Omit to close every course in use" }),
    date: LocalDate,
    from: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM")
      .openapi({ description: "HH:MM, club time; tee times from then that haven't started close" }),
    reason: z.string().trim().min(1).max(80).default("Weather"),
  })
  .openapi("CloseRestOfDay");

export const closeRestOfDayRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/closures",
  operationId: "closeRestOfDay",
  tags: ["staff"],
  security: bearer,
  description:
    "Tee sheet managers. Blocks every tee time from `from` that day and cancels the bookings on them " +
    "(audited); the closure keeps who was affected. One per course. 400 when nothing is left to close.",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: CloseRestOfDaySchema } } },
  },
  responses: {
    200: {
      description: "Closed",
      content: {
        "application/json": {
          schema: z
            .object({
              closures: z.array(
                z.object({ id: z.uuid(), courseId: z.uuid(), teeTimes: z.int(), players: z.int() }),
              ),
            })
            .openapi("RestOfDayClosed"),
        },
      },
    },
    ...errorResponses,
  },
});

export const reopenClosureRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/closures/{closureId}/reopen",
  operationId: "reopenClosure",
  tags: ["staff"],
  security: bearer,
  description: "Tee sheet managers. Unblocks the closure's times that haven't started; nobody is rebooked.",
  request: {
    params: clubSlugParams.extend({
      closureId: z.uuid().openapi({ param: { name: "closureId", in: "path" } }),
    }),
  },
  responses: {
    200: {
      description: "Reopened",
      content: { "application/json": { schema: z.object({ teeTimes: z.int() }).openapi("ClosureReopened") } },
    },
    ...errorResponses,
  },
});

export const getTeeTimeHistoryRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/tee-times/{teeTimeId}/history",
  operationId: "getTeeTimeHistory",
  tags: ["staff"],
  security: bearer,
  description: "Any staff role. Another club's tee time: 404.",
  request: {
    params: clubSlugParams.extend({
      teeTimeId: z.uuid().openapi({ param: { name: "teeTimeId", in: "path" } }),
    }),
  },
  responses: {
    200: { description: "Its history", content: { "application/json": { schema: TeeTimeHistorySchema } } },
    ...errorResponses,
  },
});

export const StaffBookingRequestSchema = z
  .union([
    z.object({
      membershipId: z.uuid(),
      position: z.int().min(1).max(5).optional(),
      playOptionId: z.uuid().optional(),
      note: z.string().trim().max(200).optional(),
    }),
    z.object({
      guestName: z.string().trim().min(1).max(80),
      hostMembershipId: z.uuid(),
      position: z.int().min(1).max(5).optional(),
      playOptionId: z.uuid().optional(),
      note: z.string().trim().max(200).optional(),
    }),
  ])
  .openapi("StaffBookingRequest", {
    description:
      "A member, or a guest with their host. Position defaults to the first open one; the play option to " +
      "the member's last choice, else the club's default.",
  });

const bookingConflicts = {
  409: {
    description:
      "`tee_time_full`, `already_booked`, `position_taken`, `idempotency_key_reused`, or `lottery` (a lottery date before its reveal)",
    content: { "application/json": { schema: ErrorSchema } },
  },
};

export const createStaffBookingRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/tee-times/{teeTimeId}/bookings",
  operationId: "createStaffBooking",
  tags: ["staff"],
  security: bearer,
  description: "Adds a member or guest. Send a fresh Idempotency-Key per attempt and reuse it on retries.",
  request: {
    params: clubSlugParams.extend({
      teeTimeId: z.uuid().openapi({ param: { name: "teeTimeId", in: "path" } }),
    }),
    headers: z.object({
      "idempotency-key": z.uuid().openapi({ param: { name: "idempotency-key", in: "header" } }),
    }),
    body: { required: true, content: { "application/json": { schema: StaffBookingRequestSchema } } },
  },
  responses: {
    201: { description: "Added", content: { "application/json": { schema: BookingSchema } } },
    200: {
      description: "A retry: the original booking",
      content: { "application/json": { schema: BookingSchema } },
    },
    ...bookingConflicts,
    ...errorResponses,
  },
});

// G4: a whole group at once, when a member phones for four.
export const StaffGroupBookingRequestSchema = z
  .object({
    players: z
      .array(
        z.union([
          z.object({ membershipId: z.uuid(), playOptionId: z.uuid().optional() }),
          z.object({
            guestName: z.string().trim().min(1).max(80),
            hostMembershipId: z.uuid(),
            playOptionId: z.uuid().optional(),
          }),
        ]),
      )
      .min(1)
      .max(5)
      .openapi({ description: "Members and guests, in the order they take the free places" }),
    note: z
      .string()
      .trim()
      .max(200)
      .optional()
      .openapi({ description: "For the starter, on the first player" }),
  })
  .openapi("StaffGroupBookingRequest", {
    description:
      "All or nothing. A member's play option defaults to their last choice, else the club's default.",
  });

export const createStaffGroupBookingRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/tee-times/{teeTimeId}/bookings/group",
  operationId: "createStaffGroupBooking",
  tags: ["staff"],
  security: bearer,
  description:
    "Adds up to a tee time's free places in one go (G4). Send a fresh Idempotency-Key per attempt and reuse it on retries.",
  request: {
    params: clubSlugParams.extend({
      teeTimeId: z.uuid().openapi({ param: { name: "teeTimeId", in: "path" } }),
    }),
    headers: z.object({
      "idempotency-key": z.uuid().openapi({ param: { name: "idempotency-key", in: "header" } }),
    }),
    body: { required: true, content: { "application/json": { schema: StaffGroupBookingRequestSchema } } },
  },
  responses: {
    201: {
      description: "Added",
      content: {
        "application/json": {
          schema: z.object({ bookings: z.array(BookingSchema) }).openapi("StaffGroupBooked"),
        },
      },
    },
    200: {
      description: "A retry: the original bookings",
      content: { "application/json": { schema: z.object({ bookings: z.array(BookingSchema) }) } },
    },
    ...bookingConflicts,
    ...errorResponses,
  },
});

// G5: move a booking or a whole group, to any day.
export const moveStaffBookingsRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/booking-moves",
  operationId: "moveStaffBookings",
  tags: ["staff"],
  security: bearer,
  description:
    "Moves players from one tee time together to another tee time at the club, on any day, each to the next " +
    "free place. All or nothing.",
  request: {
    params: clubSlugParams,
    body: {
      required: true,
      content: {
        "application/json": {
          schema: z
            .object({
              bookingIds: z
                .array(z.uuid())
                .min(1)
                .max(5)
                .openapi({ description: "All on the same tee time" }),
              teeTimeId: z.uuid(),
            })
            .openapi("MoveStaffBookings"),
        },
      },
    },
  },
  responses: {
    200: {
      description: "Moved",
      content: {
        "application/json": {
          schema: z.object({ bookings: z.array(BookingSchema) }).openapi("StaffBookingsMoved"),
        },
      },
    },
    ...bookingConflicts,
    ...errorResponses,
  },
});

const bookingParams = clubSlugParams.extend({
  bookingId: z.uuid().openapi({ param: { name: "bookingId", in: "path" } }),
});

// --- Check-in and no-shows (H1): any staff role, on the day.

export const setArrivalRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/bookings/{bookingId}/arrival",
  operationId: "setArrival",
  tags: ["staff"],
  security: bearer,
  description: "Marks a player arrived, a no-show, or neither (null, to undo). No-shows are audited.",
  request: {
    params: bookingParams,
    body: {
      required: true,
      content: {
        "application/json": {
          schema: z.object({ arrival: ArrivalSchema.nullable() }).openapi("SetArrival"),
        },
      },
    },
  },
  responses: {
    200: {
      description: "Saved",
      content: {
        "application/json": {
          schema: z.object({ id: z.uuid(), arrival: ArrivalSchema.nullable() }).openapi("ArrivalSaved"),
        },
      },
    },
    ...errorResponses,
  },
});

export const markNoShowsRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/no-shows",
  operationId: "markNoShows",
  tags: ["staff"],
  security: bearer,
  description:
    "At the end of the day: every player not checked in, on a tee time of that day that has started, becomes a no-show.",
  request: {
    params: clubSlugParams,
    body: {
      required: true,
      content: { "application/json": { schema: z.object({ date: LocalDate }).openapi("MarkNoShows") } },
    },
  },
  responses: {
    200: {
      description: "Marked",
      content: {
        "application/json": { schema: z.object({ count: z.int() }).openapi("NoShowsMarked") },
      },
    },
    ...errorResponses,
  },
});

export const moveStaffBookingRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/bookings/{bookingId}/tee-time",
  operationId: "moveStaffBooking",
  tags: ["staff"],
  security: bearer,
  description: "Moves a player to another tee time (or another position on the same one).",
  request: {
    params: bookingParams,
    body: {
      required: true,
      content: {
        "application/json": {
          schema: z
            .object({ teeTimeId: z.uuid(), position: z.int().min(1).max(5).optional() })
            .openapi("MoveBooking"),
        },
      },
    },
  },
  responses: {
    200: { description: "Moved", content: { "application/json": { schema: BookingSchema } } },
    ...bookingConflicts,
    ...errorResponses,
  },
});

export const cancelStaffBookingRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/bookings/{bookingId}",
  operationId: "cancelStaffBooking",
  tags: ["staff"],
  security: bearer,
  request: { params: bookingParams },
  responses: {
    200: {
      description: "Cancelled (the audit log keeps what it was)",
      content: { "application/json": { schema: z.object({ id: z.uuid() }).openapi("BookingCancelled") } },
    },
    ...errorResponses,
  },
});

// ---------------------------------------------------------------------------
// Staff roles: a club's owners and admins manage who is on staff. Owners manage
// every role; admins manage head_pro, starter and pro_shop. Everyone else: 404.
// ---------------------------------------------------------------------------

export const ClubPersonSchema = z
  .object({
    personId: z.uuid(),
    name: z.string(),
    memberNumber: z.string().nullable().openapi({ description: "Null for staff who aren't members" }),
    roles: z.array(StaffRoleSchema).openapi({ description: "Current staff roles; empty for members" }),
  })
  .openapi("ClubPerson");

export const ClubStaffSchema = z
  .object({
    club: ClubSummarySchema,
    staff: z.array(ClubPersonSchema),
    members: z
      .array(ClubPersonSchema)
      .openapi({ description: "Members with a Union login who aren't on staff: who can be given a role" }),
    manageableRoles: z
      .array(StaffRoleSchema)
      .openapi({ description: "The roles the signed-in person may give and remove here" }),
  })
  .openapi("ClubStaff");

const staffRoleParams = z.object({
  clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }),
  personId: z.uuid().openapi({ param: { name: "personId", in: "path" } }),
  role: StaffRoleSchema.openapi({ param: { name: "role", in: "path" } }),
});

const staffRoleResponses = {
  200: {
    description: "Done (or already so); the club's staff afterwards",
    content: { "application/json": { schema: ClubStaffSchema } },
  },
  403: {
    description:
      "`not_allowed`: that role is beyond yours (admins can't manage owners or admins); " +
      "`own_role`: you can't remove your own owner or admin role (another owner can)",
    content: { "application/json": { schema: ErrorSchema } },
  },
  409: {
    description: "`last_owner`: a club always keeps at least one owner",
    content: { "application/json": { schema: ErrorSchema } },
  },
  ...errorResponses,
};

export const getClubStaffRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/staff",
  operationId: "getClubStaff",
  tags: ["staff"],
  security: bearer,
  request: { params: z.object({ clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }) }) },
  responses: {
    200: { description: "The club's staff", content: { "application/json": { schema: ClubStaffSchema } } },
    ...errorResponses,
  },
});

export const grantStaffRoleRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/staff/{personId}/roles/{role}",
  operationId: "grantStaffRole",
  tags: ["staff"],
  security: bearer,
  description: "Gives the person this role at the club. Safe to repeat.",
  request: { params: staffRoleParams },
  responses: staffRoleResponses,
});

export const revokeStaffRoleRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/staff/{personId}/roles/{role}",
  operationId: "revokeStaffRole",
  tags: ["staff"],
  security: bearer,
  description: "Removes this role from the person at the club. Safe to repeat.",
  request: { params: staffRoleParams },
  responses: staffRoleResponses,
});

// ---------------------------------------------------------------------------
// Tee sheet setup: owners, admins and head pros. Everyone else: 404.
// ---------------------------------------------------------------------------

const LocalTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM")
  .openapi({ description: "HH:MM, club-local", example: "07:00" });

export const ScheduleDaySchema = z
  .object({
    weekday: z.int().min(0).max(6).openapi({ description: "0 = Sunday … 6 = Saturday" }),
    open: z.boolean(),
    firstTime: LocalTime,
    lastTime: LocalTime,
    intervalMinutes: z.int().min(5).max(120),
    maxPlayers: z.int().min(1).max(5),
  })
  .openapi("ScheduleDay");

const MonthDay = z
  .string()
  .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, "Use MM-DD")
  .openapi({ description: "A day of the year, MM-DD", example: "04-01" });

export const SeasonSchema = z
  .object({
    id: z.uuid(),
    name: z.string().openapi({ example: "Summer" }),
    start: MonthDay,
    end: MonthDay.openapi({ description: "Inclusive; before the start means it crosses New Year" }),
    schedule: z.array(ScheduleDaySchema).length(7),
  })
  .openapi("Season", { description: "A week for part of every year (B3)" });

const OneOffDayFields = {
  date: LocalDate,
  name: z.string().trim().min(1).max(40).openapi({ example: "Frost delay" }),
  open: z.boolean().openapi({ description: "False: no tee times that day" }),
  firstTime: LocalTime,
  lastTime: LocalTime,
  intervalMinutes: z.int().min(5).max(120),
  maxPlayers: z.int().min(1).max(5),
};

export const OneOffDaySchema = z
  .object({ id: z.uuid(), ...OneOffDayFields })
  .openapi("OneOffDay", { description: "One date with its own hours; wins over seasons (B4)" });

export const LotteryMomentSchema = z
  .object({
    daysBefore: z.int().min(1).max(60).openapi({ description: "Days before the date", example: 3 }),
    at: LocalTime.openapi({ description: "Club-local time that day", example: "18:00" }),
  })
  .openapi("LotteryMoment");

export const CourseLotterySchema = z
  .object({
    weekdays: z
      .array(z.int().min(0).max(6))
      .min(1)
      .openapi({ description: "Lottery days (0 = Sunday)", example: [6, 0] }),
    requestsOpen: LotteryMomentSchema,
    requestsClose: LotteryMomentSchema,
    reveal: LotteryMomentSchema.openapi({ description: "When the drawn sheet is shown and booking opens" }),
  })
  .openapi("CourseLottery", {
    description:
      "F1. Members can't book a lottery date until the reveal. A date counts only if its requests open after its weekday was turned on.",
  });

export const CourseSetupSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    kind: CourseKindSchema,
    holes: z.int().nullable().openapi({ example: 18, description: "Null for a simulator" }),
    schedule: z
      .array(ScheduleDaySchema)
      .length(7)
      .openapi({ description: "The default week: outside every season" }),
    seasons: z.array(SeasonSchema).openapi({ description: "In date order through the year" }),
    currentSeasonId: z.uuid().nullable().openapi({ description: "The season covering today, if any" }),
    oneOffDays: z.array(OneOffDaySchema).openapi({ description: "From today on, in date order" }),
    lottery: CourseLotterySchema.nullable().openapi({ description: "Null: no lottery on this course" }),
    setUpThrough: LocalDate.nullable().openapi({
      description: "Tee sheets exist through this date; later days are created automatically",
    }),
  })
  .openapi("CourseSetup");

export const GuestRulesSchema = z
  .object({
    maxPerTeeTime: z
      .int()
      .min(0)
      .max(4)
      .openapi({ description: "Guests one member may bring on one tee time; 0: none" }),
    maxRoundsPerYear: z
      .int()
      .min(1)
      .max(365)
      .nullable()
      .openapi({ description: "Times the same guest may play in a calendar year; null: no limit" }),
    hours: z
      .array(z.object({ weekday: z.int().min(0).max(6), earliest: LocalTime }))
      .max(7)
      .refine((h) => new Set(h.map((d) => d.weekday)).size === h.length, "One entry per weekday")
      .openapi({ description: "From what time guests may play, on the days that have one (0 = Sunday)" }),
  })
  .openapi("GuestRules", {
    description: "Limits on members' guests (E7). Staff can always add any guest.",
  });

export const ClubSetupSchema = z
  .object({
    club: ClubSummarySchema,
    courses: z.array(CourseSetupSchema).openapi({ description: "Courses in use" }),
    retiredCourses: z
      .array(z.object({ id: z.uuid(), name: z.string(), retiredAt: z.iso.datetime({ offset: true }) }))
      .openapi({ description: "Retired courses (A1): no new days; their bookings stay" }),
    playOptions: z.array(PlayOptionSchema),
    eventTypes: z.array(EventTypeSchema).openapi({ description: "In order (D4)" }),
    guestRules: GuestRulesSchema,
  })
  .openapi("ClubSetup");

export const saveGuestRulesRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/guest-rules",
  operationId: "saveGuestRules",
  tags: ["staff"],
  security: bearer,
  description: "Replaces the club's guest rules (tee sheet managers).",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: GuestRulesSchema } } },
  },
  responses: {
    200: { description: "Saved", content: { "application/json": { schema: ClubSetupSchema } } },
    ...errorResponses,
  },
});

export const getClubSetupRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/setup",
  operationId: "getClubSetup",
  tags: ["staff"],
  security: bearer,
  request: { params: clubSlugParams },
  responses: {
    200: {
      description: "Courses and schedules",
      content: { "application/json": { schema: ClubSetupSchema } },
    },
    ...errorResponses,
  },
});

export const SaveScheduleSchema = z
  .object({
    days: z
      .array(ScheduleDaySchema)
      .length(7)
      .refine((days) => new Set(days.map((d) => d.weekday)).size === 7, "One entry per weekday")
      .refine(
        (days) => days.every((d) => d.lastTime >= d.firstTime),
        "The last tee time is before the first",
      ),
    rebuildEmptyDays: z.boolean().default(true).openapi({
      description:
        "Also rebuild days already set up (from tomorrow) that have no bookings, undoing their blocks and edits",
    }),
    seasonId: z
      .uuid()
      .nullable()
      .default(null)
      .openapi({ description: "A season's week; null for the default week" }),
  })
  .openapi("SaveSchedule");

export const saveCourseScheduleRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/schedule",
  operationId: "saveCourseSchedule",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams.extend({
      courseId: z.uuid().openapi({ param: { name: "courseId", in: "path" } }),
    }),
    body: { required: true, content: { "application/json": { schema: SaveScheduleSchema } } },
  },
  responses: {
    200: {
      description: "Saved; days not yet set up were filled from it",
      content: {
        "application/json": {
          schema: z
            .object({ course: CourseSetupSchema, teeTimesCreated: z.int(), daysRebuilt: z.int() })
            .openapi("ScheduleSaved"),
        },
      },
    },
    ...errorResponses,
  },
});

export const BlockTeeTimesSchema = z
  .object({
    teeTimeIds: z.array(z.uuid()).min(1).max(500),
    reason: z.string().trim().min(1).max(80).nullable().openapi({
      description: "Why it's closed (shown to staff); null unblocks",
      example: "Course maintenance",
    }),
  })
  .openapi("BlockTeeTimes");

export const blockTeeTimesRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/tee-times/blocks",
  operationId: "blockTeeTimes",
  tags: ["staff"],
  security: bearer,
  description:
    "Blocks tee times (members can't book them; staff still can), or unblocks them with reason null.",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: BlockTeeTimesSchema } } },
  },
  responses: {
    200: {
      description: "How many tee times changed",
      content: { "application/json": { schema: z.object({ changed: z.int() }).openapi("BlockResult") } },
    },
    ...errorResponses,
  },
});

export const NewTeeTimeSchema = z
  .object({
    courseId: z.uuid(),
    date: LocalDate,
    time: LocalTime,
    maxPlayers: z.int().min(1).max(5).default(4),
  })
  .openapi("NewTeeTime");

export const addTeeTimeRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/tee-times",
  operationId: "addTeeTime",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: NewTeeTimeSchema } } },
  },
  responses: {
    201: {
      description: "Added",
      content: { "application/json": { schema: z.object({ id: z.uuid() }).openapi("TeeTimeAdded") } },
    },
    409: {
      description: "`tee_time_exists`: that course already has a tee time then",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const removeTeeTimeRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/tee-times/{teeTimeId}",
  operationId: "removeTeeTime",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams.extend({
      teeTimeId: z.uuid().openapi({ param: { name: "teeTimeId", in: "path" } }),
    }),
  },
  responses: {
    200: {
      description: "Removed",
      content: { "application/json": { schema: z.object({ id: z.uuid() }).openapi("TeeTimeRemoved") } },
    },
    409: {
      description: "`has_bookings`: move or cancel its players first",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

// ---------------------------------------------------------------------------
// Union Ops: the Union team's tools. Union operators only; anyone else gets 404.
// ---------------------------------------------------------------------------

export const OpsClubSchema = z
  .object({
    id: z.uuid(),
    slug: ClubSlug,
    name: z.string(),
    timezone: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    members: z.int().openapi({ description: "Active memberships, claimed or not" }),
    staff: z.int().openapi({ description: "People with a current staff role" }),
    signInReady: z.boolean().openapi({ description: "The club's member site can sign members in" }),
    memberSite: z.url().openapi({ example: "https://pine-valley.unionclubs.com" }),
  })
  .openapi("OpsClub");

export const OpsClubListSchema = z
  .object({
    clubs: z.array(OpsClubSchema),
    sampleDataAvailable: z
      .boolean()
      .openapi({ description: "New clubs may be filled with sample data (never in production)" }),
    memberSitePattern: z.string().openapi({ example: "https://{club}.unionclubs.com" }),
  })
  .openapi("OpsClubList");

export const NewClubSchema = z
  .object({
    slug: ClubSlug.refine(isValidClubSlug, "That address is reserved").openapi({
      description: "The club's address: {slug}.unionclubs.com",
    }),
    name: z.string().trim().min(1).max(120).openapi({ example: "Pine Valley Golf Club" }),
    timezone: z.string().min(1).openapi({ example: "America/Chicago" }),
    sampleData: z.boolean().default(false).openapi({
      description: "Add a sample roster, course and tee sheet, plus the sample cast. Not in production.",
    }),
  })
  .openapi("NewClub");

export const listOpsClubsRoute = createRoute({
  method: "get",
  path: "/ops/clubs",
  operationId: "listOpsClubs",
  tags: ["ops"],
  security: bearer,
  responses: {
    200: { description: "Every club", content: { "application/json": { schema: OpsClubListSchema } } },
    ...errorResponses,
  },
});

export const createOpsClubRoute = createRoute({
  method: "post",
  path: "/ops/clubs",
  operationId: "createOpsClub",
  tags: ["ops"],
  security: bearer,
  description: "Creates a club and registers its member site with the shared sign-in site.",
  request: { body: { required: true, content: { "application/json": { schema: NewClubSchema } } } },
  responses: {
    201: { description: "Created", content: { "application/json": { schema: OpsClubSchema } } },
    409: {
      description: "`slug_taken`: another club has that address",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

// ---------------------------------------------------------------------------
// Member groups (E1): owners, admins, head pros and the pro shop. Everyone
// else: 404. Every write returns the roster afterwards.
// ---------------------------------------------------------------------------

export const BookingRuleSchema = z
  .object({
    weekday: z.int().min(0).max(6).openapi({ description: "0 = Sunday … 6 = Saturday" }),
    from: LocalTime,
    to: z
      .string()
      .regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, "Use HH:MM, or 24:00 for the end of the day")
      .openapi({
        description: "HH:MM, club-local, exclusive; 24:00 is the end of the day",
        example: "24:00",
      }),
    daysAhead: z.int().min(0).max(60).openapi({ description: "How many days before the date it opens" }),
    opensAt: LocalTime.openapi({ description: "Club-local time it opens that day", example: "07:00" }),
  })
  .openapi("BookingRule", {
    description: "A time band in which a group's members may book, and how far ahead",
  });

export const MemberGroupSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    isDefault: z.boolean().openapi({ description: "New members join this group; it can't be deleted" }),
    members: z.int().openapi({ description: "Active members in the group" }),
    lotteryWeight: z.int().min(1).max(5).openapi({
      description:
        "Odds in the lottery draw (F3): a weight-3 group is 3× as likely as weight 1 to draw first",
    }),
    rules: z
      .array(BookingRuleSchema)
      .openapi({ description: "When its members may book, by weekday and time" }),
  })
  .openapi("MemberGroup");

export const RosterEntrySchema = z
  .object({
    membershipId: z.uuid(),
    name: z.string(),
    memberNumber: z.string(),
    groupId: z.uuid(),
    noShows: z.int().openapi({ description: "No-shows in the last 12 months (H1)" }),
  })
  .openapi("RosterEntry");

export const MemberRosterSchema = z
  .object({
    groups: z.array(MemberGroupSchema),
    members: z.array(RosterEntrySchema),
  })
  .openapi("MemberRoster", { description: "Every active member, sorted by name, and the club's groups" });

const GroupNameSchema = z
  .object({ name: z.string().trim().min(1).max(40).openapi({ example: "Junior" }) })
  .openapi("MemberGroupName");

const memberGroupParams = clubSlugParams.extend({
  groupId: z.uuid().openapi({ param: { name: "groupId", in: "path" } }),
});

const rosterResponse = {
  200: {
    description: "The roster afterwards",
    content: { "application/json": { schema: MemberRosterSchema } },
  },
};

export const getMemberRosterRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/roster",
  operationId: "getMemberRoster",
  tags: ["staff"],
  security: bearer,
  request: { params: clubSlugParams },
  responses: {
    200: { description: "The roster", content: { "application/json": { schema: MemberRosterSchema } } },
    ...errorResponses,
  },
});

export const createMemberGroupRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/member-groups",
  operationId: "createMemberGroup",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: GroupNameSchema } } },
  },
  responses: {
    ...rosterResponse,
    409: { description: "`name_taken`", content: { "application/json": { schema: ErrorSchema } } },
    ...errorResponses,
  },
});

export const renameMemberGroupRoute = createRoute({
  method: "patch",
  path: "/staff/clubs/{clubSlug}/member-groups/{groupId}",
  operationId: "renameMemberGroup",
  tags: ["staff"],
  security: bearer,
  request: {
    params: memberGroupParams,
    body: { required: true, content: { "application/json": { schema: GroupNameSchema } } },
  },
  responses: {
    ...rosterResponse,
    409: { description: "`name_taken`", content: { "application/json": { schema: ErrorSchema } } },
    ...errorResponses,
  },
});

export const deleteMemberGroupRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/member-groups/{groupId}",
  operationId: "deleteMemberGroup",
  tags: ["staff"],
  security: bearer,
  description: "Only an empty group that isn't the default can go.",
  request: { params: memberGroupParams },
  responses: {
    ...rosterResponse,
    409: {
      description: "`default_group`, or `not_empty`: move its members first",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const SetGroupMembersSchema = z
  .object({ membershipIds: z.array(z.uuid()).min(1).max(1000) })
  .openapi("SetGroupMembers");

export const setGroupMembersRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/member-groups/{groupId}/members",
  operationId: "setGroupMembers",
  tags: ["staff"],
  security: bearer,
  description:
    "Puts these members in the group (each member is in exactly one). New bookings follow the new " +
    "group's rules; existing bookings stay. Any unknown member: 404, and nothing changes.",
  request: {
    params: memberGroupParams,
    body: { required: true, content: { "application/json": { schema: SetGroupMembersSchema } } },
  },
  responses: { ...rosterResponse, ...errorResponses },
});

export const SaveBookingRulesSchema = z
  .object({ rules: z.array(BookingRuleSchema).max(70) })
  .openapi("SaveBookingRules", { description: "The group's whole week. Days with no band: no booking." });

export const saveBookingRulesRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/member-groups/{groupId}/rules",
  operationId: "saveBookingRules",
  tags: ["staff"],
  security: bearer,
  description:
    "Replaces the group's booking rules. Staff can always book anyone; the rules are for members. " +
    "A band must end after it starts, and a day's bands can't overlap (400).",
  request: {
    params: memberGroupParams,
    body: { required: true, content: { "application/json": { schema: SaveBookingRulesSchema } } },
  },
  responses: { ...rosterResponse, ...errorResponses },
});

// ---------------------------------------------------------------------------
// Play options (G2): the club's list (Setup: tee sheet managers) and each
// player's choice (staff for anyone; a member for their own booking).
// ---------------------------------------------------------------------------

export const SavePlayOptionsSchema = z
  .object({
    options: z
      .array(z.object({ id: z.uuid().nullable(), name: z.string().trim().min(1).max(40) }))
      .min(1)
      .max(12)
      .openapi({ description: "In order; id null for a new one. Left out: deleted, or archived if used." }),
    defaultIndex: z.int().min(0).openapi({ description: "Which of `options` is the default" }),
  })
  .openapi("SavePlayOptions");

export const savePlayOptionsRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/play-options",
  operationId: "savePlayOptions",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: SavePlayOptionsSchema } } },
  },
  responses: {
    200: {
      description: "Saved; the club's setup",
      content: { "application/json": { schema: ClubSetupSchema } },
    },
    ...errorResponses,
  },
});

const PlayOptionChoiceSchema = z.object({ playOptionId: z.uuid() }).openapi("PlayOptionChoice");
const bookingIdParams = z.object({
  clubSlug: ClubSlug.openapi({ param: { name: "clubSlug", in: "path" } }),
  bookingId: z.uuid().openapi({ param: { name: "bookingId", in: "path" } }),
});
const playOptionSet = {
  200: {
    description: "Saved",
    content: {
      "application/json": {
        schema: PlayOptionChoiceSchema.extend({ id: z.uuid() }).openapi("PlayOptionSet"),
      },
    },
  },
  ...errorResponses,
};

export const setStaffPlayOptionRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/bookings/{bookingId}/play-option",
  operationId: "setStaffPlayOption",
  tags: ["staff"],
  security: bearer,
  request: {
    params: bookingIdParams,
    body: { required: true, content: { "application/json": { schema: PlayOptionChoiceSchema } } },
  },
  responses: playOptionSet,
});

export const setMemberPlayOptionRoute = createRoute({
  method: "put",
  path: "/member/clubs/{clubSlug}/bookings/{bookingId}/play-option",
  operationId: "setMemberPlayOption",
  tags: ["member"],
  security: bearer,
  description: "How the member, or a guest they brought (E7), is playing. Until the time starts.",
  request: {
    params: bookingIdParams,
    body: { required: true, content: { "application/json": { schema: PlayOptionChoiceSchema } } },
  },
  responses: {
    ...playOptionSet,
    409: {
      description:
        "`already_started`, or the member's group rules no longer allow this time (with the reason)",
      content: { "application/json": { schema: ErrorSchema } },
    },
  },
});

// ---------------------------------------------------------------------------
// Booking notes (G3): staff for anyone; a member for their own booking.
// ---------------------------------------------------------------------------

const BookingNoteSchema = z
  .object({
    note: z
      .string()
      .trim()
      .max(200)
      .openapi({ description: "Empty clears it", example: "Meeting a guest at the range" }),
  })
  .openapi("BookingNote");
const bookingNoteSet = {
  200: {
    description: "Saved",
    content: {
      "application/json": {
        schema: z.object({ id: z.uuid(), note: z.string().nullable() }).openapi("BookingNoteSet"),
      },
    },
  },
  ...errorResponses,
};

export const setStaffBookingNoteRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/bookings/{bookingId}/note",
  operationId: "setStaffBookingNote",
  tags: ["staff"],
  security: bearer,
  request: {
    params: bookingIdParams,
    body: { required: true, content: { "application/json": { schema: BookingNoteSchema } } },
  },
  responses: bookingNoteSet,
});

export const setMemberBookingNoteRoute = createRoute({
  method: "put",
  path: "/member/clubs/{clubSlug}/bookings/{bookingId}/note",
  operationId: "setMemberBookingNote",
  tags: ["member"],
  security: bearer,
  description: "The member's note on their own booking, for the starter. Until the time starts.",
  request: {
    params: bookingIdParams,
    body: { required: true, content: { "application/json": { schema: BookingNoteSchema } } },
  },
  responses: {
    ...bookingNoteSet,
    409: {
      description:
        "`already_started`, or the member's group rules no longer allow this time (with the reason)",
      content: { "application/json": { schema: ErrorSchema } },
    },
  },
});

// ---------------------------------------------------------------------------
// Events (D1, D3): every staff role sees them; tee sheet managers (owners,
// admins, head pros) create, change and delete them. Every write returns the
// list afterwards.
// ---------------------------------------------------------------------------

const EventEnd = z
  .string()
  .regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, "Use HH:MM, or 24:00 for the end of the day")
  .openapi({ description: "HH:MM, club-local, exclusive; 24:00 is the end of the day", example: "18:00" });

export const EventInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80).openapi({ example: "Men's league" }),
    typeId: z.uuid().openapi({ description: "One of the club's event types" }),
    showName: z.boolean().default(false).openapi({
      description: "Members see the event's name on the tee sheet; otherwise just “Not available” (D5)",
    }),
    courseId: z.uuid(),
    date: LocalDate,
    from: LocalTime,
    to: EventEnd,
  })
  .openapi("EventInput", { description: "The event takes the course's tee times from `from` up to `to`" });

export const EventSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    type: EventTypeSchema,
    showName: z.boolean().openapi({ description: "Whether members see its name (D5)" }),
    courseId: z.uuid(),
    courseName: z.string(),
    date: LocalDate,
    from: z.string(),
    to: z.string(),
    teeTimes: z.int().openapi({ description: "How many tee times it has reserved" }),
    players: z.int().openapi({ description: "Players staff have placed on its times (D6)" }),
    series: z
      .object({
        id: z.uuid(),
        firstDate: LocalDate,
        lastDate: LocalDate,
        skipped: z.array(LocalDate).openapi({ description: "Dates taken out of the series" }),
      })
      .nullable()
      .openapi({ description: "The weekly series this date belongs to (D2), if any" }),
    conflicts: z
      .array(
        z.object({
          bookingId: z.uuid(),
          teeTimeId: z.uuid(),
          localTime: z.string(),
          name: z.string(),
          guestOf: z.string().nullable(),
        }),
      )
      .openapi({
        description:
          "Players booked on its times before it held them (D3). Nothing is cancelled automatically; players placed since (D6) aren't conflicts.",
      }),
  })
  .openapi("Event");

export const EventListSchema = z
  .object({
    events: z.array(EventSchema).openapi({ description: "From the given date on, in order" }),
    courses: z.array(z.object({ id: z.uuid(), name: z.string() })),
    types: z.array(EventTypeSchema).openapi({ description: "The club's event types, in order" }),
    canManage: z.boolean().openapi({ description: "Whether you can create, change and delete events" }),
  })
  .openapi("EventList");

// ---------------------------------------------------------------------------
// Week view (H4): a week at a glance, per course, for planning staffing.
// ---------------------------------------------------------------------------

const PlacesSchema = z.object({
  players: z.int().openapi({ description: "Players booked (events' players included)" }),
  places: z.int().openapi({ description: "Places on its tee times (players per time, summed)" }),
});

export const StaffWeekSchema = z
  .object({
    club: ClubSummarySchema,
    start: LocalDate.openapi({ description: "The Monday the week starts on" }),
    courses: z.array(z.object({ id: z.uuid(), name: z.string(), kind: CourseKindSchema })),
    days: z
      .array(
        z.object({
          date: LocalDate,
          courses: z.array(
            z.object({
              courseId: z.uuid(),
              teeTimes: z.int().openapi({ description: "0: closed, or not set up yet" }),
              ...PlacesSchema.shape,
              morning: PlacesSchema.openapi({ description: "Tee times before 12:00 (club time)" }),
              afternoon: PlacesSchema.openapi({ description: "From 12:00" }),
              blocked: z.int().openapi({ description: "Tee times blocked by hand (events not counted)" }),
              events: z.array(
                z.object({
                  id: z.uuid(),
                  name: z.string(),
                  from: z.string(),
                  to: z.string(),
                  type: EventTypeSchema,
                }),
              ),
              lottery: z
                .enum(["upcoming", "requests_open", "awaiting_reveal", "revealed"])
                .nullable()
                .openapi({ description: "When the date is a lottery date: where it stands (F1–F4)" }),
            }),
          ),
        }),
      )
      .length(7)
      .openapi({ description: "Monday to Sunday" }),
  })
  .openapi("StaffWeek");

export const getStaffWeekRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/week",
  operationId: "getStaffWeek",
  tags: ["staff"],
  security: bearer,
  description: "Any staff role. The week (Monday to Sunday, club time) containing the date.",
  request: {
    params: clubSlugParams,
    query: z.object({ date: LocalDate.openapi({ param: { name: "date", in: "query" } }) }),
  },
  responses: {
    200: { description: "The week", content: { "application/json": { schema: StaffWeekSchema } } },
    ...errorResponses,
  },
});

const eventListResponse = {
  200: { description: "The events", content: { "application/json": { schema: EventListSchema } } },
};
const eventParams = clubSlugParams.extend({
  eventId: z.uuid().openapi({ param: { name: "eventId", in: "path" } }),
});

export const listEventsRoute = createRoute({
  method: "get",
  path: "/staff/clubs/{clubSlug}/events",
  operationId: "listEvents",
  tags: ["staff"],
  security: bearer,
  request: {
    params: clubSlugParams,
    query: z.object({
      from: LocalDate.optional().openapi({
        param: { name: "from", in: "query" },
        description: "Default: today at the club",
      }),
    }),
  },
  responses: { ...eventListResponse, ...errorResponses },
});

export const createEventRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/events",
  operationId: "createEvent",
  tags: ["staff"],
  security: bearer,
  description: "Creates the event and reserves its tee times (now, and as days are set up).",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: EventInputSchema } } },
  },
  responses: { ...eventListResponse, ...errorResponses },
});

export const updateEventRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/events/{eventId}",
  operationId: "updateEvent",
  tags: ["staff"],
  security: bearer,
  description: "Changes the event; the times it no longer covers open again.",
  request: {
    params: eventParams,
    body: { required: true, content: { "application/json": { schema: EventInputSchema } } },
  },
  responses: { ...eventListResponse, ...errorResponses },
});

export const deleteEventRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/events/{eventId}",
  operationId: "deleteEvent",
  tags: ["staff"],
  security: bearer,
  description: "Deletes the event; its tee times open again. Bookings are untouched.",
  request: { params: eventParams },
  responses: { ...eventListResponse, ...errorResponses },
});

export const EventSeriesInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80).openapi({ example: "Men's league" }),
    typeId: z.uuid().openapi({ description: "One of the club's event types" }),
    showName: z.boolean().default(false).openapi({
      description: "Members see the event's name on the tee sheet; otherwise just “Not available” (D5)",
    }),
    courseId: z.uuid(),
    firstDate: LocalDate.openapi({ description: "Its weekday is the series' weekday" }),
    lastDate: LocalDate.openapi({ description: "Up to a year after the first" }),
    from: LocalTime,
    to: EventEnd,
  })
  .openapi("EventSeriesInput", {
    description: "Every week on the first date's weekday, from the first to the last date",
  });

const eventSeriesParams = clubSlugParams.extend({
  seriesId: z.uuid().openapi({ param: { name: "seriesId", in: "path" } }),
});

export const createEventSeriesRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/event-series",
  operationId: "createEventSeries",
  tags: ["staff"],
  security: bearer,
  description: "A weekly event: one event per week, each reserving its tee times.",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: EventSeriesInputSchema } } },
  },
  responses: { ...eventListResponse, ...errorResponses },
});

export const updateEventSeriesRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/event-series/{seriesId}",
  operationId: "updateEventSeries",
  tags: ["staff"],
  security: bearer,
  description: "Changes the series from today on; past dates stay as they were. Skipped dates stay skipped.",
  request: {
    params: eventSeriesParams,
    body: { required: true, content: { "application/json": { schema: EventSeriesInputSchema } } },
  },
  responses: { ...eventListResponse, ...errorResponses },
});

export const deleteEventSeriesRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/event-series/{seriesId}",
  operationId: "deleteEventSeries",
  tags: ["staff"],
  security: bearer,
  description:
    "Ends the series: its dates from today on go (their tee times open). Past dates stay. Deleting one date " +
    "(DELETE events/{id}) skips just that date.",
  request: { params: eventSeriesParams },
  responses: { ...eventListResponse, ...errorResponses },
});

export const SeasonInputSchema = z
  .object({
    name: z.string().trim().min(1).max(40),
    start: MonthDay,
    end: MonthDay,
    rebuildEmptyDays: z.boolean().default(true).openapi({
      description: "Rebuild future days without bookings, as days can move in or out of the season",
    }),
  })
  .openapi("SeasonInput", { description: "A new season starts with a copy of the default week" });

const seasonParams = clubSlugParams.extend({
  courseId: z.uuid().openapi({ param: { name: "courseId", in: "path" } }),
});
const setupResponse = {
  200: {
    description: "Saved; the club's setup",
    content: { "application/json": { schema: ClubSetupSchema } },
  },
};

export const createSeasonRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/seasons",
  operationId: "createSeason",
  tags: ["staff"],
  security: bearer,
  request: {
    params: seasonParams,
    body: { required: true, content: { "application/json": { schema: SeasonInputSchema } } },
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const updateSeasonRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/seasons/{seasonId}",
  operationId: "updateSeason",
  tags: ["staff"],
  security: bearer,
  description: "Renames it or moves its dates. Seasons can't overlap (400).",
  request: {
    params: seasonParams.extend({ seasonId: z.uuid().openapi({ param: { name: "seasonId", in: "path" } }) }),
    body: { required: true, content: { "application/json": { schema: SeasonInputSchema } } },
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const deleteSeasonRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/seasons/{seasonId}",
  operationId: "deleteSeason",
  tags: ["staff"],
  security: bearer,
  description: "Its dates go back to the default week; future days without bookings are rebuilt.",
  request: {
    params: seasonParams.extend({ seasonId: z.uuid().openapi({ param: { name: "seasonId", in: "path" } }) }),
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const OneOffDayInputSchema = z
  .object(OneOffDayFields)
  .openapi("OneOffDayInput", { description: "A date after today; one per course" });

const oneOffDayParams = seasonParams.extend({
  dayId: z.uuid().openapi({ param: { name: "dayId", in: "path" } }),
});
const oneOffDaySaved = {
  200: {
    description: "Saved; the date was rebuilt unless it has bookings",
    content: {
      "application/json": {
        schema: z
          .object({
            applied: z.boolean().openapi({
              description: "False when the day has bookings, so its tee times were left as they were",
            }),
            setup: ClubSetupSchema,
          })
          .openapi("OneOffDaySaved"),
      },
    },
  },
};

export const createOneOffDayRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/one-off-days",
  operationId: "createOneOffDay",
  tags: ["staff"],
  security: bearer,
  description: "A date already given its own hours, or today or earlier, is refused (400).",
  request: {
    params: seasonParams,
    body: { required: true, content: { "application/json": { schema: OneOffDayInputSchema } } },
  },
  responses: { ...oneOffDaySaved, ...errorResponses },
});

export const updateOneOffDayRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/one-off-days/{dayId}",
  operationId: "updateOneOffDay",
  tags: ["staff"],
  security: bearer,
  request: {
    params: oneOffDayParams,
    body: { required: true, content: { "application/json": { schema: OneOffDayInputSchema } } },
  },
  responses: { ...oneOffDaySaved, ...errorResponses },
});

export const deleteOneOffDayRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/one-off-days/{dayId}",
  operationId: "deleteOneOffDay",
  tags: ["staff"],
  security: bearer,
  description: "The date goes back to its season's or the default week (rebuilt if it has no bookings).",
  request: { params: oneOffDayParams },
  responses: { ...setupResponse, ...errorResponses },
});

// --- Managing courses (A1): tee sheet managers add, rename, retire and bring back courses.

export const AddCourseSchema = z
  .object({
    name: z.string().trim().min(1).max(80).openapi({ example: "Ocean" }),
    kind: CourseKindSchema.default("course"),
    holes: z
      .union([z.literal(9), z.literal(18)])
      .optional()
      .openapi({ description: "Courses only (default 18); a simulator has none" }),
    playersPerTeeTime: z.int().min(1).max(5).default(4),
  })
  .openapi("AddCourse", {
    description:
      "A course starts on the standard week (07:00–15:00, every 9 minutes); a simulator on 09:00–21:00, every hour",
  });

export const addCourseRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/courses",
  operationId: "addCourse",
  tags: ["staff"],
  security: bearer,
  description: "A name another course has is refused (400).",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: AddCourseSchema } } },
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const renameCourseRoute = createRoute({
  method: "patch",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}",
  operationId: "renameCourse",
  tags: ["staff"],
  security: bearer,
  request: {
    params: seasonParams,
    body: {
      required: true,
      content: {
        "application/json": {
          schema: z.object({ name: z.string().trim().min(1).max(80) }).openapi("RenameCourse"),
        },
      },
    },
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const retireCourseRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/retired",
  operationId: "retireCourse",
  tags: ["staff"],
  security: bearer,
  description:
    "Retires a course (true): no new days, and its future days without bookings go; days with bookings stay. " +
    "False brings it back.",
  request: {
    params: seasonParams,
    body: {
      required: true,
      content: {
        "application/json": { schema: z.object({ retired: z.boolean() }).openapi("RetireCourse") },
      },
    },
  },
  responses: {
    200: {
      description: "Saved",
      content: {
        "application/json": {
          schema: z
            .object({
              daysRemoved: z.int(),
              daysKept: z.int().openapi({ description: "Future days kept because they have bookings" }),
              setup: ClubSetupSchema,
            })
            .openapi("CourseRetired"),
        },
      },
    },
    ...errorResponses,
  },
});

// --- Event types (D4): each club's own, with a colour.

export const SaveEventTypesSchema = z
  .object({
    types: z
      .array(
        z.object({
          id: z.uuid().optional().openapi({ description: "Omit for a new type" }),
          name: z.string().trim().min(1).max(40),
          colour: EventColourSchema,
        }),
      )
      .min(1)
      .max(20),
  })
  .openapi("SaveEventTypes", {
    description: "The whole list, in order. A type left out is removed, or archived if events use it.",
  });

export const saveEventTypesRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/event-types",
  operationId: "saveEventTypes",
  tags: ["staff"],
  security: bearer,
  description: "Tee sheet managers. Two types with the same name: 400.",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: SaveEventTypesSchema } } },
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const saveCourseLotteryRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/lottery",
  operationId: "saveCourseLottery",
  tags: ["staff"],
  security: bearer,
  description:
    "Turns the course's lottery on, or changes it. 400 if requests don't open before they close, or close after the reveal.",
  request: {
    params: seasonParams,
    body: { required: true, content: { "application/json": { schema: CourseLotterySchema } } },
  },
  responses: { ...setupResponse, ...errorResponses },
});

export const turnOffCourseLotteryRoute = createRoute({
  method: "delete",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/lottery",
  operationId: "turnOffCourseLottery",
  tags: ["staff"],
  security: bearer,
  description: "Its dates go back to normal booking.",
  request: { params: seasonParams },
  responses: { ...setupResponse, ...errorResponses },
});

// --- Lottery requests (F2): a member's group asks for a time range on a lottery date.

const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM");
const LotteryRequestChange = {
  earliest: ClockTime.openapi({ example: "07:30" }),
  latest: ClockTime.openapi({ example: "09:00" }),
  laterOk: z.boolean().default(false),
  partnerIds: z
    .array(z.uuid())
    .max(4)
    .default([])
    .openapi({ description: "Other members' membership ids (from playingPartners)" }),
};
export const LotteryRequestInputSchema = z
  .object({ courseId: z.uuid(), date: LocalDate, ...LotteryRequestChange })
  .openapi("LotteryRequestInput");
export const LotteryRequestChangeSchema = z.object(LotteryRequestChange).openapi("LotteryRequestChange");

const lotteryRequestSaved = {
  200: {
    description: "Saved; the request as the member sees it",
    content: { "application/json": { schema: LotteryRequestSchema } },
  },
  409: {
    description:
      "`requests_closed` (not a lottery day, or requests aren't open), or `already_requested` (someone in the group is already in a request that day)",
    content: { "application/json": { schema: ErrorSchema } },
  },
};
const lotteryRequestParams = clubSlugParams.extend({
  requestId: z.uuid().openapi({ param: { name: "requestId", in: "path" } }),
});

export const createLotteryRequestRoute = createRoute({
  method: "post",
  path: "/member/clubs/{clubSlug}/lottery-requests",
  operationId: "createLotteryRequest",
  tags: ["member"],
  security: bearer,
  description: "The signed-in member organizes it; one request per member per date.",
  request: {
    params: clubSlugParams,
    body: { required: true, content: { "application/json": { schema: LotteryRequestInputSchema } } },
  },
  responses: { ...lotteryRequestSaved, ...errorResponses },
});

export const updateLotteryRequestRoute = createRoute({
  method: "put",
  path: "/member/clubs/{clubSlug}/lottery-requests/{requestId}",
  operationId: "updateLotteryRequest",
  tags: ["member"],
  security: bearer,
  description: "The organizer changes it until requests close. Partners can't (404).",
  request: {
    params: lotteryRequestParams,
    body: { required: true, content: { "application/json": { schema: LotteryRequestChangeSchema } } },
  },
  responses: { ...lotteryRequestSaved, ...errorResponses },
});

export const leaveLotteryRequestRoute = createRoute({
  method: "delete",
  path: "/member/clubs/{clubSlug}/lottery-requests/{requestId}",
  operationId: "leaveLotteryRequest",
  tags: ["member"],
  security: bearer,
  description: "The organizer withdraws the whole request; a partner leaves it. Until requests close.",
  request: { params: lotteryRequestParams },
  responses: {
    200: {
      description: "Withdrawn or left",
      content: {
        "application/json": {
          schema: z.object({ outcome: z.enum(["withdrawn", "left"]) }).openapi("LotteryRequestLeft"),
        },
      },
    },
    409: {
      description: "`requests_closed`",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const playingPartnersRoute = createRoute({
  method: "get",
  path: "/member/clubs/{clubSlug}/playing-partners",
  operationId: "playingPartners",
  tags: ["member"],
  security: bearer,
  description: "Other active members matching a name (2+ letters), up to 10. Name and member number only.",
  request: {
    params: clubSlugParams,
    query: z.object({ q: z.string().max(60).openapi({ example: "rob" }) }),
  },
  responses: {
    200: {
      description: "Matching members",
      content: {
        "application/json": {
          schema: z
            .object({
              partners: z.array(
                z.object({
                  membershipId: z.uuid(),
                  name: z.string(),
                  memberNumber: z.string().nullable().openapi({ description: "Tells two of a name apart" }),
                }),
              ),
            })
            .openapi("PlayingPartners"),
        },
      },
    },
    ...errorResponses,
  },
});

// --- The draw (F3): order and place a lottery date's requests.

export const drawLotteryRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/lottery-draws",
  operationId: "drawLottery",
  tags: ["staff"],
  security: bearer,
  description:
    "Runs (or re-runs) the draw for a date, between requests closing and the reveal. It also runs on its own " +
    "within the hour after requests close. Owners, admins, head pros and the pro shop.",
  request: {
    params: seasonParams,
    body: {
      required: true,
      content: { "application/json": { schema: z.object({ date: LocalDate }).openapi("DrawLottery") } },
    },
  },
  responses: {
    200: {
      description: "Drawn",
      content: {
        "application/json": {
          schema: z.object({ placed: z.int(), unplaced: z.int() }).openapi("LotteryDrawn"),
        },
      },
    },
    409: {
      description: "`not_lottery_day`, `requests_open` (wait until they close), or `revealed`",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

// --- Adjust and reveal (F4): staff move drawn groups, then reveal (placements become bookings).

export const moveLotteryPlacementRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/lottery-requests/{requestId}/placement",
  operationId: "moveLotteryPlacement",
  tags: ["staff"],
  security: bearer,
  description:
    "Moves a drawn group to another tee time on its course and date, or takes it out (null), before the reveal.",
  request: {
    params: clubSlugParams.extend({
      requestId: z.uuid().openapi({ param: { name: "requestId", in: "path" } }),
    }),
    body: {
      required: true,
      content: {
        "application/json": {
          schema: z.object({ teeTimeId: z.uuid().nullable() }).openapi("LotteryPlacement"),
        },
      },
    },
  },
  responses: {
    200: {
      description: "Moved",
      content: {
        "application/json": {
          schema: z
            .object({ requestId: z.uuid(), teeTimeId: z.uuid().nullable() })
            .openapi("LotteryPlacementMoved"),
        },
      },
    },
    409: {
      description: "`not_drawn`, `revealed`, `no_room`, or `unavailable` (blocked or held for an event)",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const revealLotteryRoute = createRoute({
  method: "post",
  path: "/staff/clubs/{clubSlug}/courses/{courseId}/lottery-reveals",
  operationId: "revealLottery",
  tags: ["staff"],
  security: bearer,
  description:
    "Reveals a date now: books every placed group and opens the rest of the day to members. It also happens on " +
    "its own within the hour after the reveal time.",
  request: {
    params: seasonParams,
    body: {
      required: true,
      content: { "application/json": { schema: z.object({ date: LocalDate }).openapi("RevealLottery") } },
    },
  },
  responses: {
    200: {
      description: "Revealed",
      content: {
        "application/json": {
          schema: z.object({ groups: z.int(), players: z.int() }).openapi("LotteryRevealed"),
        },
      },
    },
    409: {
      description: "`not_lottery_day`, `requests_open`, or `revealed`",
      content: { "application/json": { schema: ErrorSchema } },
    },
    ...errorResponses,
  },
});

export const setGroupLotteryWeightRoute = createRoute({
  method: "put",
  path: "/staff/clubs/{clubSlug}/member-groups/{groupId}/lottery-weight",
  operationId: "setGroupLotteryWeight",
  tags: ["staff"],
  security: bearer,
  description: "How the group's requests are weighted in the lottery draw (1–5).",
  request: {
    params: memberGroupParams,
    body: {
      required: true,
      content: {
        "application/json": {
          schema: z.object({ weight: z.int().min(1).max(5) }).openapi("GroupLotteryWeight"),
        },
      },
    },
  },
  responses: { ...rosterResponse, ...errorResponses },
});

export const routes = [
  healthRoute,
  getPublicClubRoute,
  getMeRoute,
  getMemberTeeSheetRoute,
  getStaffTeeSheetRoute,
  getStaffWeekRoute,
  createMemberBookingRoute,
  moveMemberBookingRoute,
  cancelMemberBookingRoute,
  addMemberGuestRoute,
  getMemberCoursesRoute,
  syncMemberRoundRoute,
  getMemberRoundRoute,
  getClubSetupRoute,
  saveCourseScheduleRoute,
  blockTeeTimesRoute,
  addTeeTimeRoute,
  removeTeeTimeRoute,
  searchRosterRoute,
  createStaffBookingRoute,
  createStaffGroupBookingRoute,
  getMemberDetailRoute,
  getTeeTimeHistoryRoute,
  closeRestOfDayRoute,
  reopenClosureRoute,
  moveStaffBookingsRoute,
  setArrivalRoute,
  markNoShowsRoute,
  moveStaffBookingRoute,
  cancelStaffBookingRoute,
  getClubStaffRoute,
  grantStaffRoleRoute,
  revokeStaffRoleRoute,
  getMemberRosterRoute,
  createMemberGroupRoute,
  renameMemberGroupRoute,
  deleteMemberGroupRoute,
  setGroupMembersRoute,
  saveBookingRulesRoute,
  savePlayOptionsRoute,
  setStaffPlayOptionRoute,
  setMemberPlayOptionRoute,
  setStaffBookingNoteRoute,
  setMemberBookingNoteRoute,
  listEventsRoute,
  createEventRoute,
  updateEventRoute,
  deleteEventRoute,
  createEventSeriesRoute,
  updateEventSeriesRoute,
  deleteEventSeriesRoute,
  createSeasonRoute,
  updateSeasonRoute,
  deleteSeasonRoute,
  createOneOffDayRoute,
  updateOneOffDayRoute,
  deleteOneOffDayRoute,
  saveEventTypesRoute,
  saveGuestRulesRoute,
  addCourseRoute,
  renameCourseRoute,
  retireCourseRoute,
  saveCourseLotteryRoute,
  turnOffCourseLotteryRoute,
  createLotteryRequestRoute,
  updateLotteryRequestRoute,
  leaveLotteryRequestRoute,
  playingPartnersRoute,
  drawLotteryRoute,
  setGroupLotteryWeightRoute,
  moveLotteryPlacementRoute,
  revealLotteryRoute,
  listOpsClubsRoute,
  createOpsClubRoute,
] as const;

export const openApiInfo = {
  openapi: "3.1.0",
  info: { title: "Union API", version: "0.1.0" },
  servers: [{ url: "https://api.unionclubs.com" }, { url: "http://localhost:8787" }],
};

// Types for TypeScript clients come straight from the schemas: no codegen step.
export type ApiError = z.infer<typeof ErrorSchema>;
export type ClubSummary = z.infer<typeof ClubSummarySchema>;
export type Me = z.infer<typeof MeSchema>;
export type MemberTeeSheet = z.infer<typeof MemberTeeSheetSchema>;
export type StaffTeeSheet = z.infer<typeof StaffTeeSheetSchema>;
export type StaffTeeTime = z.infer<typeof StaffTeeTimeSchema>;
export type MemberTeeTime = z.infer<typeof MemberTeeTimeSchema>;
export type Booking = z.infer<typeof BookingSchema>;
export type PublicClub = z.infer<typeof PublicClubSchema>;
export type Course = z.infer<typeof CourseSchema>;
export type Scorecard = z.infer<typeof ScorecardSchema>;
export type RoundSync = z.infer<typeof RoundSyncSchema>;
export type HoleScore = z.infer<typeof HoleScoreSchema>;
export type ScheduleDay = z.infer<typeof ScheduleDaySchema>;
export type CourseSetup = z.infer<typeof CourseSetupSchema>;
export type ClubSetup = z.infer<typeof ClubSetupSchema>;
export type SaveSchedule = z.input<typeof SaveScheduleSchema>;
export type BlockTeeTimes = z.infer<typeof BlockTeeTimesSchema>;
export type NewTeeTime = z.input<typeof NewTeeTimeSchema>;
export type RosterMember = z.infer<typeof RosterMemberSchema>;
export type StaffBookingRequest = z.input<typeof StaffBookingRequestSchema>;
export type StaffPlayer = StaffTeeTime["players"][number];
export type StaffRole = z.infer<typeof StaffRoleSchema>;
export type ClubPerson = z.infer<typeof ClubPersonSchema>;
export type ClubStaff = z.infer<typeof ClubStaffSchema>;
export type OpsClub = z.infer<typeof OpsClubSchema>;
export type OpsClubList = z.infer<typeof OpsClubListSchema>;
export type NewClub = z.input<typeof NewClubSchema>;
export type MemberGroup = z.infer<typeof MemberGroupSchema>;
export type RosterEntry = z.infer<typeof RosterEntrySchema>;
export type MemberRoster = z.infer<typeof MemberRosterSchema>;
export type BookingRule = z.infer<typeof BookingRuleSchema>;
export type PlayOption = z.infer<typeof PlayOptionSchema>;
export type SavePlayOptions = z.input<typeof SavePlayOptionsSchema>;
export type ClubEvent = z.infer<typeof EventSchema>;
export type EventList = z.infer<typeof EventListSchema>;
export type EventInput = z.input<typeof EventInputSchema>;
export type EventType = z.infer<typeof EventTypeSchema>;
export type EventSeriesInput = z.input<typeof EventSeriesInputSchema>;
export type Season = z.infer<typeof SeasonSchema>;
export type SeasonInput = z.input<typeof SeasonInputSchema>;
export type OneOffDay = z.infer<typeof OneOffDaySchema>;
export type Arrival = z.infer<typeof ArrivalSchema>;
export type AddCourse = z.input<typeof AddCourseSchema>;
export type EventColour = z.infer<typeof EventColourSchema>;
export type SaveEventTypes = z.input<typeof SaveEventTypesSchema>;
export type GuestRules = z.infer<typeof GuestRulesSchema>;
export type MemberGuest = z.input<typeof MemberGuestSchema>;
export type CourseKind = z.infer<typeof CourseKindSchema>;
export type Closure = z.infer<typeof ClosureSchema>;
export type CloseRestOfDay = z.input<typeof CloseRestOfDaySchema>;
export type StaffWeek = z.infer<typeof StaffWeekSchema>;
export type TeeTimeHistory = z.infer<typeof TeeTimeHistorySchema>;
export type MemberDetail = z.infer<typeof MemberDetailSchema>;
export type MemberTeeTimeSummary = z.infer<typeof MemberTeeTimeSummarySchema>;
export type StaffGroupBookingRequest = z.input<typeof StaffGroupBookingRequestSchema>;
export type OneOffDayInput = z.input<typeof OneOffDayInputSchema>;
export type OneOffDaySaved = { applied: boolean; setup: ClubSetup };
export type CourseLottery = z.infer<typeof CourseLotterySchema>;
export type LotteryMoment = z.infer<typeof LotteryMomentSchema>;
export type LotteryStatus = z.infer<typeof LotteryStatusSchema>;
export type LotteryRequest = z.infer<typeof LotteryRequestSchema>;
export type LotteryRequestInput = z.input<typeof LotteryRequestInputSchema>;
export type LotteryRequestChange = z.input<typeof LotteryRequestChangeSchema>;
export type PlayingPartner = { membershipId: string; name: string; memberNumber: string | null };
